import { motion } from 'framer-motion';
import { ClipboardCheck, Coins, TrendingUp, Check, Sparkles, Leaf } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { HOW } from '@/constants/testIds';
import { useI18n } from '@/i18n/I18nProvider';

export default function HowItWorks() {
  const { m } = useI18n();
  const h = m.how;
  return (
    <section id="how-it-works" data-testid={HOW.root} className="relative section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
            {h.eyebrow}
          </div>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-[44px] tracking-[-0.015em] leading-[1.05]">
            {h.heading}
          </h2>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed">{h.sub}</p>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {h.steps.map((s, i) => (
            <motion.div
              key={s.tag}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              data-testid={`${HOW.stepPrefix}-${i + 1}`}
            >
              <Card className="relative h-full overflow-hidden bg-card border border-border rounded-2xl p-6 sm:p-7 hover:border-primary/40 card-hover">
                <div
                  aria-hidden
                  className="absolute -top-16 -right-16 h-44 w-44 rounded-full opacity-25 blur-3xl"
                  style={{ background: 'hsla(86, 70%, 62%, 0.55)' }}
                />
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-full border border-primary/40 bg-primary/15 text-primary flex items-center justify-center font-display text-sm">
                      0{i + 1}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {s.tag}
                    </div>
                  </div>
                </div>
                <h3 className="relative mt-5 font-display text-[22px] leading-[1.15]">
                  {s.title}
                </h3>
                <p className="relative mt-3 text-sm text-muted-foreground leading-relaxed">
                  {s.body}
                </p>

                {/* Step visual */}
                <div className="relative mt-5">
                  {i === 0 && <ReadinessVisual title={s.mockTitle} score={s.mockScore} hint={s.mockHint} bullets={s.bullets} />}
                  {i === 1 && <FinancingVisual chipA={s.chipA} chipB={s.chipB} bullets={s.bullets} />}
                  {i === 2 && <RevenueVisual a={s.metricA} av={s.metricAValue} b={s.metricB} bv={s.metricBValue} bs={s.metricBSub} bullets={s.bullets} />}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Outcomes */}
        <div className="mt-12">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
            {h.outcomesTitle}
          </div>
          <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {h.outcomes.map((o) => (
              <div
                key={o.k}
                className="rounded-xl border border-border bg-secondary/40 p-4"
              >
                <div className="font-display text-base leading-tight">{o.k}</div>
                <div className="mt-1 text-xs text-muted-foreground leading-snug">{o.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ReadinessVisual({ title, score, hint, bullets }) {
  const pct = Math.min(100, Math.max(0, score || 0));
  const r = 36;
  const c = 2 * Math.PI * r;
  const offset = c - (c * pct) / 100;
  return (
    <div className="rounded-xl border border-border bg-background/60 p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{title}</div>
        <ClipboardCheck className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-2 flex items-center gap-4">
        <svg width="88" height="88" viewBox="0 0 88 88">
          <circle cx="44" cy="44" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="6" />
          <circle
            cx="44" cy="44" r={r}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={offset}
            transform="rotate(-90 44 44)"
          />
          <text x="44" y="48" textAnchor="middle" fontFamily="Fraunces" fontSize="22" fill="hsl(var(--foreground))">
            {pct}
          </text>
          <text x="44" y="62" textAnchor="middle" fontSize="8" fill="hsl(var(--muted-foreground))">/100</text>
        </svg>
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-accent">
            <Leaf className="h-3 w-3" />
            {hint}
          </div>
          <ul className="mt-2 space-y-1.5">
            {bullets.map((b) => (
              <li key={b} className="flex items-center justify-between text-[11px]">
                <span className="text-foreground/80">{b}</span>
                <span className="relative h-1.5 w-14 rounded-full bg-border overflow-hidden">
                  <span
                    className="absolute inset-y-0 left-0 rounded-full bg-primary"
                    style={{ width: `${60 + ((b.length * 7) % 30)}%` }}
                  />
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function FinancingVisual({ chipA, chipB, bullets }) {
  return (
    <div className="rounded-xl border border-border bg-background/60 p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Klymart pre‑financing</div>
        <Coins className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-primary/40 bg-primary/10 px-2.5 py-2 text-[10px] uppercase tracking-wider text-primary">
          {chipA}
        </div>
        <div className="rounded-lg border border-border bg-secondary px-2.5 py-2 text-[10px] uppercase tracking-wider text-muted-foreground">
          {chipB}
        </div>
      </div>
      <ul className="mt-3 space-y-1.5">
        {bullets.map((b) => (
          <li key={b} className="flex items-center gap-2 text-[11px] text-foreground/85">
            <span className="h-4 w-4 rounded-full border border-primary/40 bg-primary/10 inline-flex items-center justify-center">
              <Check className="h-2.5 w-2.5 text-primary" />
            </span>
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RevenueVisual({ a, av, b, bv, bs, bullets }) {
  // simple animated bar chart for Sales impact
  const bars = [12, 16, 22, 30, 38, 48, 60];
  return (
    <div className="rounded-xl border border-border bg-background/60 p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{a}</div>
        <TrendingUp className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-2 flex items-end justify-between gap-1 h-14">
        {bars.map((b, i) => (
          <motion.div
            key={`bar-${i}-${b}`}
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 + i * 0.06, duration: 0.45 }}
            className="w-3 rounded-t-sm"
            style={{ height: b, background: i === bars.length - 1 ? 'hsl(var(--primary))' : 'hsla(86,70%,62%,0.5)', transformOrigin: 'bottom' }}
          />
        ))}
      </div>
      <div className="mt-1 font-display text-2xl text-primary leading-none">{av}</div>
      <div className="mt-4 h-px bg-border" />
      <div className="mt-3 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{b}</div>
        <Sparkles className="h-4 w-4 text-accent" />
      </div>
      <div className="mt-1.5 flex items-center gap-3">
        <div className="relative h-2 flex-1 rounded-full bg-border overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: '35%' }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.4 }}
            className="absolute inset-y-0 left-0 rounded-full bg-accent"
          />
        </div>
        <div className="font-display text-sm">{bv}</div>
      </div>
      <p className="mt-2 text-[10px] text-muted-foreground leading-snug">{bs}</p>
      <ul className="mt-3 grid grid-cols-3 gap-1.5">
        {bullets.map((bl) => (
          <li key={bl} className="rounded-md border border-border bg-secondary/60 px-2 py-1 text-[9px] uppercase tracking-wider text-foreground/80 text-center">
            {bl}
          </li>
        ))}
      </ul>
    </div>
  );
}
